/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/FeaturedBanners/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import styles from "./FeaturedBanners.module.css";

import { IconButton, CircularProgress, Backdrop } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { ReactComponent as EditIcon } from "../../assets/icons/FeaturedBanners/EditIcon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/FeaturedBanners/DeleteIcon.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/FeaturedBanners/AddIcon.svg";

import { useState, useEffect, useRef } from "react";
import AssetImage from "../AssetImage";
import { useNavigate } from "react-router";
import BannerFormModal from "../../modals/BannerFormModal";
import ConfirmationModal from "../../modals/ConfirmationModal";
import { getBanners, deleteBanner } from "../../logic/banner";
import { useTranslation } from "react-i18next";

const FeaturedBanners = ({ permissions, currentUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [featuredBanners, setFeaturedBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const confirmationDialogRef = useRef(null);
  const autoRotateIntervalRef = useRef(null);

  const isTablet = window.innerWidth <= 991;
  const isMediumMobile = window.innerWidth <= 768;
  const isSmallMobile = window.innerWidth <= 480;
  const isNotComputer = isTablet || isMediumMobile || isSmallMobile;

  useEffect(() => {
    fetchBanners();
  }, []);

  // Auto-rotation effect
  useEffect(() => {
    if (featuredBanners.length > 1) {
      autoRotateIntervalRef.current = setInterval(() => {
        setCurrentImageIndex((prevIndex) =>
          prevIndex === featuredBanners.length - 1 ? 0 : prevIndex + 1,
        );
      }, 10000); // 10 seconds

      // Cleanup interval on unmount or when banners change
      return () => {
        if (autoRotateIntervalRef.current) {
          clearInterval(autoRotateIntervalRef.current);
        }
      };
    }
  }, [featuredBanners.length]);

  // Cleanup interval when component unmounts
  useEffect(() => {
    return () => {
      if (autoRotateIntervalRef.current) {
        clearInterval(autoRotateIntervalRef.current);
      }
    };
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      setError(null);
      const banners = await getBanners(navigate);
      setFeaturedBanners(Array.isArray(banners) ? banners : []);
    } catch (error) {
      logger.error("Error fetching banners:", { err: sanitizeError(error) });
      setError(t("components.featuredSections.errorLoadingBanners"));
      setFeaturedBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBanner = () => {
    setEditMode(false);
    setSelectedBanner(null);
    setModalOpen(true);
  };

  const handleEditBanner = (banner) => {
    setEditMode(true);
    setSelectedBanner(banner);
    setModalOpen(true);
  };

  const handleDeleteBanner = (banner) => {
    setBannerToDelete(banner);
    setDeleteConfirmationOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!bannerToDelete) {
      return;
    }

    try {
      setIsDeleting(true);
      const result = await deleteBanner(bannerToDelete._id, navigate);
      if (result) {
        const deletedIndex = featuredBanners.findIndex(
          (b) => b._id === bannerToDelete._id,
        );

        if (deletedIndex !== -1) {
          if (featuredBanners.length === 1) {
            setCurrentImageIndex(0);
          } else if (currentImageIndex === deletedIndex) {
            setCurrentImageIndex((prevIndex) =>
              prevIndex === 0 ? featuredBanners.length - 2 : prevIndex - 1,
            );
          } else if (currentImageIndex > deletedIndex) {
            setCurrentImageIndex((prevIndex) => prevIndex - 1);
          } else {
            // currentImageIndex < deletedIndex -> no index adjustment needed
          }
        }

        await fetchBanners();
        logger.info("Banner deleted successfully");
      } else {
        logger.error("Failed to delete banner");
      }
    } catch (error) {
      logger.error("Error deleting banner:", { err: sanitizeError(error) });
    } finally {
      setIsDeleting(false);
      setBannerToDelete(null);
    }
  };

  const handleCloseDeleteConfirmation = () => {
    setDeleteConfirmationOpen(false);
    setBannerToDelete(null);
    setIsDeleting(false);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditMode(false);
    setSelectedBanner(null);
  };

  const handleBannerCreated = async (bannerData) => {
    await fetchBanners();
    logger.info("Banner created successfully");
  };

  const handleBannerUpdated = async (bannerData) => {
    await fetchBanners();
    logger.info("Banner updated successfully");
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? featuredBanners.length - 1 : prevIndex - 1,
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === featuredBanners.length - 1 ? 0 : prevIndex + 1,
    );
  };

  if (loading) {
    return (
      <div className={styles.featuredSection}>
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          }}
          open={loading}
        >
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: "#4eabe7",
            }}
          />
        </Backdrop>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.featuredSection}>
        <div className={styles.errorMessage}>{error}</div>
      </div>
    );
  }

  return (
    <div className={styles.featuredSection}>
      <div className={styles.featuredHeader}>
        {permissions?.find((permission) =>
          permission.hasOwnProperty("editHomepage"),
        ).editHomepage &&
          currentUser &&
          currentUser?.tipoDeUsuario !== "4" &&
          currentUser?.tipoDeUsuario !== "12" &&
          !isNotComputer && (
            <button
              className={styles.createBannerButton}
              onClick={handleCreateBanner}
              disabled={featuredBanners.length >= 4}
            >
              <AddIcon />
              {t("components.featuredBanners.createBannerButton")}
            </button>
          )}
      </div>

      {featuredBanners.length === 0 ? (
        <div className={styles.noBannersMessage}>
          {t("components.featuredBanners.noBannersMessage")}
        </div>
      ) : (
        <>
          <div className={styles.carouselContainer}>
            <IconButton
              className={styles.carouselArrow}
              onClick={handlePreviousImage}
            >
              <ChevronLeftIcon />
            </IconButton>

            <div className={styles.carouselContent}>
              <div className={styles.carouselTrack}>
                {featuredBanners.map((banner, index) => (
                  <div
                    key={banner._id}
                    className={`${styles.carouselItem} ${
                      index === currentImageIndex ? styles.active : ""
                    }`}
                  >
                    {banner.redirectUrl ? (
                      <a
                        href={banner.redirectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.bannerLink}
                      >
                        <AssetImage
                          src={banner.imageUrl}
                          alt={t("common.bannerImageAlt", { id: banner._id })}
                          className={styles.bannerImage}
                        />
                      </a>
                    ) : (
                      <div className={styles.bannerImageContainer}>
                        <AssetImage
                          src={banner.imageUrl}
                          alt={t("common.bannerImageAlt", { id: banner._id })}
                          className={styles.bannerImage}
                        />
                      </div>
                    )}
                    {permissions?.find((permission) =>
                      permission.hasOwnProperty("editHomepage"),
                    ).editHomepage &&
                      currentUser &&
                      currentUser?.tipoDeUsuario !== "4" &&
                      currentUser?.tipoDeUsuario !== "12" &&
                      !isNotComputer && (
                        <div className={styles.bannerActions}>
                          <button
                            className={styles.editBannerButton}
                            onClick={() => handleEditBanner(banner)}
                            title={t(
                              "components.featuredBanners.editBannerButton",
                            )}
                          >
                            <EditIcon />
                          </button>
                          <button
                            className={styles.deleteBannerButton}
                            onClick={() => handleDeleteBanner(banner)}
                            title={t(
                              "components.featuredBanners.deleteBannerButton",
                            )}
                          >
                            <DeleteIcon />
                          </button>
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>

            <IconButton
              className={styles.carouselArrow}
              onClick={handleNextImage}
            >
              <ChevronRightIcon />
            </IconButton>
          </div>

          <div className={styles.carouselIndicators}>
            {featuredBanners.map((_, index) => (
              <button
                key={index}
                className={`${styles.indicator} ${
                  index === currentImageIndex ? styles.activeIndicator : ""
                }`}
                onClick={() => setCurrentImageIndex(index)}
              />
            ))}
          </div>
        </>
      )}

      <BannerFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        banner={selectedBanner}
        editMode={editMode}
        onBannerCreated={handleBannerCreated}
        onBannerUpdated={handleBannerUpdated}
      />

      {deleteConfirmationOpen && (
        <ConfirmationModal
          dialogRef={confirmationDialogRef}
          open={deleteConfirmationOpen}
          onClose={handleCloseDeleteConfirmation}
          onConfirm={handleConfirmDelete}
          title={t("components.confirmationModal.title")}
          message={t("components.confirmationModal.message")}
          confirmButtonText={t("components.confirmationModal.confirm")}
          cancelButtonText={t("components.confirmationModal.cancel")}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default FeaturedBanners;
